<template>
  <v-container fluid>

    <v-data-table
      :options.sync="options"
      :footer-props="{ itemsPerPageOptions: [10, 25, 50, -1] }"
      :headers="headers"
      :items="filteredCustomers"
      :single-expand="true"
      :custom-sort="customSort"
    >
      <template v-slot:top>
        <v-toolbar
          class="mt-4"
          flat
        >
          <CustomerForm v-on:create="addCustomer" />

          <v-btn
              icon
              outlined
              @click="syncIsAirport"
              class="mx-2"
          >
            <v-tooltip
                bottom
            >
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                    v-bind="attrs"
                    v-on="on"
                    color="primary"
                >
                  mdi-airport
                </v-icon>
              </template>
              sync is airport
            </v-tooltip>
          </v-btn>

          <v-btn
              icon
              outlined
              @click="refreshData"
              class="mx-2"
          >
            <v-tooltip
                bottom
            >
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                    v-bind="attrs"
                    v-on="on"
                    color="primary"
                >
                  mdi-refresh
                </v-icon>
              </template>
              refresh customer list
            </v-tooltip>
          </v-btn>
        </v-toolbar>
      </template>

      <template v-slot:body.prepend>
        <tr class="table-filter-row">
          <td>
            <v-text-field
              v-model="filters.id"
              placeholder="id"
              dense
              outlined
              clearable
              hide-details
              class="table-column-filter"
            />
          </td>
          <td>
            <v-text-field
              v-model="filters.name"
              placeholder="name"
              dense
              outlined
              clearable
              hide-details
              class="table-column-filter"
            />
          </td>
          <td>
            <v-text-field
              v-model="filters.fullName"
              placeholder="full name"
              dense
              outlined
              clearable
              hide-details
              class="table-column-filter"
            />
          </td>
          <td>
            <v-text-field
              v-model="filters.dgAlias"
              placeholder="DG alias"
              dense
              outlined
              clearable
              hide-details
              class="table-column-filter"
            />
          </td>
          <td>
            <v-autocomplete
              v-model="filters.applicationId"
              :items="applicationFilterOptions"
              :filter="relationFilterSearch"
              :menu-props="{ closeOnContentClick: true }"
              item-text="name"
              item-value="application_id"
              dense
              outlined
              clearable
              hide-details
              class="table-column-filter"
            />
          </td>
          <td>
            <v-autocomplete
              v-model="filters.applicationType"
              :items="applicationTypeFilterItems"
              :filter="relationFilterSearch"
              :menu-props="{ closeOnContentClick: true }"
              item-text="text"
              item-value="value"
              dense
              outlined
              clearable
              hide-details
              class="table-column-filter"
            />
          </td>
          <td>
            <v-autocomplete
              v-model="filters.tripbits"
              :items="tripbitsFilterOptions"
              :filter="relationFilterSearch"
              :menu-props="{ closeOnContentClick: true }"
              dense
              outlined
              clearable
              hide-details
              multiple
              chips
              small-chips
              deletable-chips
              :search-input.sync="relationSearch.tripbits"
              @change="onRelationFilterChange('tripbits', $event)"
              class="table-column-filter"
            />
          </td>
          <td>
            <v-select
              v-model="filters.isAirport"
              :items="booleanFilterItems"
              :menu-props="{ closeOnContentClick: true }"
              item-text="text"
              item-value="value"
              dense
              outlined
              clearable
              hide-details
              class="table-column-filter"
            />
          </td>
          <td>
            <v-select
              v-model="filters.hasLocalSettings"
              :items="booleanFilterItems"
              :menu-props="{ closeOnContentClick: true }"
              item-text="text"
              item-value="value"
              dense
              outlined
              clearable
              hide-details
              class="table-column-filter"
            />
          </td>
          <td>
            <v-select
              v-model="filters.isNative"
              :items="booleanFilterItems"
              :menu-props="{ closeOnContentClick: true }"
              item-text="text"
              item-value="value"
              dense
              outlined
              clearable
              hide-details
              class="table-column-filter"
            />
          </td>
          <td>
            <v-select
              v-model="filters.isActive"
              :items="booleanFilterItems"
              :menu-props="{ closeOnContentClick: true }"
              item-text="text"
              item-value="value"
              dense
              outlined
              clearable
              hide-details
              class="table-column-filter"
            />
          </td>
          <td>
            <v-text-field
              v-model="filters.applicationUrl"
              placeholder="application url"
              dense
              outlined
              clearable
              hide-details
              class="table-column-filter"
            />
          </td>
          <td />
        </tr>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn
            icon @click="createCompositeCustomer(item.id)"
            :disabled="item.has_same_name_composite_customer"
        >
          <v-tooltip>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                  v-bind="attrs"
                  v-on="on"
                  color="primary"
              >
                mdi-plus
              </v-icon>
            </template>
            create composite customer
          </v-tooltip>
        </v-btn>
        <v-btn icon @click="remove(item.id)">
          <v-icon
              color="red"
          >
            mdi-delete
          </v-icon>
        </v-btn>
      </template>

      <template v-slot:item.name="{ item }">
        <v-edit-dialog
            @save="save(itemClone, item)"
            @open="itemClone = {...item}"
        >
          <div v-if="item.name" class="font-weight-black">{{ item.name }}</div>
          <div v-else class="font-italic font-weight-thin red--text" v-text="'empty'"/>
          <template v-slot:input>
            <v-text-field
                v-model="itemClone.name"
                label="Edit"
                single-line
                counter
            />
          </template>
        </v-edit-dialog>
      </template>

      <template v-slot:item.full_name="{ item }">
          <div
              v-if="customerExternalAttribute(item.application_id, 'full_name')"
              class="font-weight-black"
          >
            {{ customerExternalAttribute(item.application_id, 'full_name') }}
          </div>
          <div v-else class="font-italic font-weight-thin red--text" v-text="'empty'"/>
      </template>

      <template v-slot:item.dg_alias="{ item }">
        <v-edit-dialog
          @save="save(itemClone, item)"
          @open="itemClone = {...item}"
        >
          <div v-if="item.dg_alias" class="font-weight-black">{{ item.dg_alias }}</div>
          <div v-else class="font-italic font-weight-thin red--text" v-text="'empty'"/>
          <template v-slot:input>
            <v-text-field
              v-model="itemClone.dg_alias"
              label="Edit"
              single-line
              counter
            />
          </template>
        </v-edit-dialog>
      </template>

      <template v-slot:item.application_url="{ item }">
          <div
              v-if="customerExternalAttribute(item.application_id, 'application_url')"
              class="font-weight-black"
          >
            {{ customerExternalAttribute(item.application_id, 'application_url') }}
          </div>
          <div v-else class="font-italic font-weight-thin red--text" v-text="'empty'"/>
      </template>

      <template v-slot:item.airport="{ item }">
        <v-switch
            v-model="item.is_airport"
            :disabled="!hasApplicationBinding(item) || !item.has_local_settings"
            @change="item.has_local_settings ? save(item, item) : null"
        />
      </template>

      <template v-slot:item.local_settings="{ item }">
        <v-switch
            v-model="item.has_local_settings"
            :disabled="!hasApplicationBinding(item)"
            @change="save(item, item)"
        />
      </template>

      <template v-slot:item.application_id="{ item }">
        <v-edit-dialog
          @save="save(itemClone, item)"
          @open="itemClone = {...item}"
        >
          <div
              v-if="applicationName(item.application_id)"
              class="font-weight-black"
          >
            {{ applicationName(item.application_id) }}
          </div>
          <div
              v-else-if="item.application_id"
              class="font-weight-black"
          >
            {{ item.application_id }}
          </div>
          <div v-else class="font-italic font-weight-thin red--text" v-text="'empty'"/>
          <template v-slot:input>
            <div class="edit-dialog-input">
              <v-select
                v-model="itemClone.application_id"
                :items="applicationsList"
                item-text="name"
                item-value="application_id"
                label="Application"
                clearable
                dense
              />
            </div>
          </template>
        </v-edit-dialog>
      </template>

      <template v-slot:item.application_type="{ item }">
        <v-edit-dialog
          @save="save(itemClone, item)"
          @open="itemClone = {...item}"
        >
          <div v-if="item.application_type" class="font-weight-black">
            {{ item.application_type }}
          </div>
          <div v-else class="font-italic font-weight-thin red--text" v-text="'empty'"/>
          <template v-slot:input>
            <div class="edit-dialog-input">
              <v-select
                v-model="itemClone.application_type"
                :items="applicationTypes"
                label="Application type"
                clearable
                dense
              />
            </div>
          </template>
        </v-edit-dialog>
      </template>

      <template v-slot:item.tripbits_customers="{ item }">
            <v-chip
              v-for="tripbitsCustomer in tripbitsCustomers(item.application_id)"
              :key="tripbitsCustomer.id"
              class="ma-0"
              small
          >
            {{ tripbitsCustomer.name }}
            {{ tripbitsCustomer.routemap
              ? `(RM: ${tripbitsCustomer.routemap})`
              : ''
            }}
          </v-chip>
      </template>

      <template v-slot:item.active="{ item }">
        <v-switch
            v-model="item.is_active"
            @change="save(item, item)"
        />
      </template>

      <template v-slot:item.native="{ item }">
        <v-switch
            v-model="item.is_native"
            @change="save(item, item)"
        />
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex';
import CustomerForm from '../components/CustomerForm.vue';
import relationFilter, { EMPTY_FILTER_OPTION } from '../mixins/relationFilter';

export default {
  name: 'Customers',
  components: {
    CustomerForm,
  },
  mixins: [
    relationFilter,
  ],
  data: () => ({
    itemClone: {},
    customers: [],
    customersExternal: [],
    applicationsList: [],
    tripbitsCustomersList: [],
    headers: [
      { text: 'id', value: 'id', width: '112px' },
      { text: 'name', value: 'name' },
      { text: 'full name', value: 'full_name' },
      { text: 'DG alias', value: 'dg_alias' },
      { text: 'app', value: 'application_id', width: '100px' },
      { text: 'app type', value: 'application_type', width: '120px' },
      { text: 'tripbits customer sets', value: 'tripbits_customers' },
      { text: 'WWF', value: 'airport', width: '88px' },
      { text: 'local settings', value: 'local_settings', width: '88px' },
      { text: 'is native', value: 'native', width: '88px' },
      { text: 'is active', value: 'active', width: '88px' },
      { text: 'application url', value: 'application_url' },
      { text: '', value: 'actions', sortable: false },
    ],
    options: {
      itemsPerPage: 10,
      sortBy: ['id'],
      sortDesc: [false],
      mustSort: true,
      multiSort: false,
    },
    filters: {
      id: '',
      name: '',
      fullName: '',
      dgAlias: '',
      applicationId: null,
      applicationType: null,
      tripbits: null,
      applicationUrl: '',
      isAirport: null,
      hasLocalSettings: null,
      isNative: null,
      isActive: null,
    },
    relationSearch: {
      tripbits: null,
    },
    booleanFilterItems: [
      { text: 'Yes', value: true },
      { text: 'No', value: false },
    ],
    applicationTypes: ['WWF', 'Cloud', 'Native', 'WebApp'],
  }),

  created() {
    this.refreshData();
  },

  methods: {
    ...mapActions({
      setSnack: 'snackbar/setState',
    }),

    matchFilter(value, filter) {
      if (!filter) return true;
      return String(value).toLowerCase().includes(String(filter).toLowerCase());
    },

    parseIdFilterValues(filter) {
      return String(filter || '')
        .trim()
        .split(/[\s,]+/)
        .map((value) => value.trim())
        .filter(Boolean);
    },

    matchIdFilter(value, filter) {
      const filterValues = this.parseIdFilterValues(filter);

      if (!filterValues.length) return true;

      return filterValues.includes(String(value ?? '').trim());
    },

    toBool(value) {
      if (value === true || value === 1 || value === '1') return true;
      if (value === false || value === 0 || value === '0') return false;
      return null;
    },

    tripbitsFilterLabel(tripbitsCustomer) {
      if (!tripbitsCustomer) return '';

      return tripbitsCustomer.routemap
        ? `${tripbitsCustomer.name} (RM: ${tripbitsCustomer.routemap})`
        : `${tripbitsCustomer.name}`;
    },

    async refreshData() {
      await this.fetchCustomers();
      await this.fetchApplicationsList();
      await this.fetchTripbitsCustomerList();
    },

    async fetchCustomers() {
      await this.$requestHelpers
        .get('api/admin/customer')
        .then((response) => {
          this.customers = response.data;
        }).catch((error) => {
          if (error.response) {
            if (error.response.status === 403) {
              this.$router.push({ name: 'root' });
            }
          }
        });

      await this.$requestHelpers
        .get('api/admin/customer/getCustomersExternal')
        .then((response) => {
          this.customersExternal = response.data.map(
            (el) => ({
              full_name: el.full_name,
              application_id: el.application_id,
              application_url: el.application_url,
              is_airport: el.is_airport,
            }),
          );
        });
    },

    async fetchApplicationsList() {
      await this.$requestHelpers
        .get('api/admin/customer/getApplicationsList')
        .then((response) => {
          this.applicationsList = (response.data.result || []).map(
            (item) => ({
              application_id: parseInt(item.application_id, 10),
              name: item.name,
            }),
          );
        });
    },

    applicationName(applicationId) {
      if (!applicationId) return null;
      // eslint-disable-next-line
      const app = this.applicationsList.find((el) => el.application_id == applicationId);
      return app ? app.name : null;
    },

    async fetchTripbitsCustomerList() {
      await this.$requestHelpers
        .get('api/admin/customer/getTripbitsCustomerSetList')
        .then((response) => {
          this.tripbitsCustomersList = response.data.map(
            (el) => ({
              id: el.id,
              application_id: el.application_id,
              name: el.name,
              routemap: el.routemap,
            }),
          );
        });
    },

    async syncIsAirport() {
      await this.$requestHelpers
        .post('api/admin/customer/syncIsAirport')
        .then(() => {
          this.refreshData();
        });
    },

    addCustomer(customer) {
      this.customers.push(customer);
    },

    save(itemClone, item) {
      this.$requestHelpers
        .patch(`api/admin/customer/${item.id}`, itemClone)
        .then((response) => {
          Object.assign(item, response.data.result);
        });
    },

    remove(id) {
      const customer = this.customers.find((el) => el.id === id);
      this.$confirm(`Are you sure you want to delete customer ${customer.name}?`).then((res) => {
        if (!res) return;

        this.$requestHelpers
          .delete(`api/admin/customer/${id}`)
          .then(() => {
            this.customers = this.customers.filter((e) => e.id !== id);
          });
      });
    },

    createCompositeCustomer(customerId) {
      this.$requestHelpers
        .post('api/admin/compositeCustomer/createByCustomer',
          {
            customer_id: customerId,
          }).then((response) => {
          if (response.data.success) {
            this.setSnack({
              color: 'green',
              text: 'composite customer was created',
            });

            const customerIndex = this.customers
              .findIndex((el) => el.id === customerId);

            this.customers[customerIndex].has_same_name_composite_customer = true;
          }
        });
    },

    tripbitsCustomers(applicationId) {
      return this.tripbitsCustomersList.filter((el) => el.application_id === applicationId);
    },

    customerExternalAttribute(applicationId, attribute) {
      // eslint-disable-next-line
      const customer = this.customersExternal.find((el) => el.application_id == applicationId);

      if (!customer) return null;
      return customer[attribute] || null;
    },

    hasApplicationBinding(item) {
      return !!item.application_id
        // eslint-disable-next-line eqeqeq
        && this.customersExternal.some((el) => el.application_id == item.application_id);
    },

    applicationIdMatched(customer) {
      if (this.filters.applicationId === null) return true;
      if (this.filters.applicationId === 0) {
        return !customer.application_id;
      }
      // eslint-disable-next-line eqeqeq
      return customer.application_id == this.filters.applicationId;
    },

    customSort(items, sortBy, sortDesc) {
      if (!sortBy.length) return items;

      const key = sortBy[0];
      const desc = sortDesc[0];

      return [...items].sort((a, b) => {
        let valA;
        let valB;

        if (key === 'application_id') {
          valA = (
            this.applicationName(a.application_id) || ''
          ).toLowerCase();
          valB = (
            this.applicationName(b.application_id) || ''
          ).toLowerCase();
        } else {
          valA = a[key];
          valB = b[key];
        }

        if (valA == null) valA = '';
        if (valB == null) valB = '';

        if (typeof valA === 'string') {
          const cmp = valA.localeCompare(valB);
          return desc ? -cmp : cmp;
        }

        let cmp = 0;
        if (valA < valB) cmp = -1;
        else if (valA > valB) cmp = 1;
        return desc ? -cmp : cmp;
      });
    },
  },

  computed: {
    filteredCustomers() {
      return this.customers.filter((customer) => {
        const id = String(customer.id || '');
        const name = String(customer.name || '');
        const dgAlias = String(customer.dg_alias || '');
        const fullName = String(this.customerExternalAttribute(customer.application_id, 'full_name') || '');
        const applicationUrl = String(this.customerExternalAttribute(customer.application_id, 'application_url') || '');
        const tripbits = this.tripbitsCustomers(customer.application_id)
          .map((el) => this.tripbitsFilterLabel(el));
        const tripbitsMatched = this.matchRelationFilter(this.filters.tripbits, tripbits);
        const isAirportMatched = this.filters.isAirport === null
          ? true
          : this.toBool(customer.is_airport) === this.filters.isAirport;
        const hasLocalSettingsMatched = this.filters.hasLocalSettings === null
          ? true
          : this.toBool(customer.has_local_settings) === this.filters.hasLocalSettings;
        const isNativeMatched = this.filters.isNative === null
          ? true
          : this.toBool(customer.is_native) === this.filters.isNative;
        const isActiveMatched = this.filters.isActive === null
          ? true
          : this.toBool(customer.is_active) === this.filters.isActive;
        const applicationTypeMatched = this.filters.applicationType === null
          ? true
          : (customer.application_type || '') === this.filters.applicationType;

        return this.matchIdFilter(id, this.filters.id)
          && this.matchFilter(name, this.filters.name)
          && this.matchFilter(fullName, this.filters.fullName)
          && this.matchFilter(dgAlias, this.filters.dgAlias)
          && this.applicationIdMatched(customer)
          && applicationTypeMatched
          && tripbitsMatched
          && this.matchFilter(applicationUrl, this.filters.applicationUrl)
          && isAirportMatched
          && hasLocalSettingsMatched
          && isNativeMatched
          && isActiveMatched;
      });
    },

    tripbitsFilterOptions() {
      return this.relationFilterOptions(
        this.tripbitsCustomersList.map((item) => this.tripbitsFilterLabel(item)),
      );
    },

    applicationFilterOptions() {
      return [
        { application_id: 0, name: EMPTY_FILTER_OPTION },
        ...[...this.applicationsList]
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
      ];
    },

    applicationTypeFilterItems() {
      return [
        { text: '<empty>', value: '' },
        ...[...this.applicationTypes]
          .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
          .map((type) => ({ text: type, value: type })),
      ];
    },
  },
};
</script>

<style scoped>
.table-filter-row > td {
  padding: 4px 8px !important;
}

.table-column-filter {
  max-width: 100%;
}

/* A bit more breathing room when editing the app / app type selects,
   with the field centred vertically. */
.edit-dialog-input {
  padding: 15px 5px 10px;
}
</style>
